import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { Drug, DrugCreate, DrugLight } from "../models/drug";
import { MedicalServiceUpdateVariations } from "src/app/backend/models/medical-service";
import { map } from "rxjs/operators";
import { sntTranslate } from "src/app/support/translate";
import { sanitizeHTML } from "src/app/support/sanitize";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { first } from "lodash";
import { omitFalsyProperties } from "src/app/support/object";
import { HTTP_CONTEXT_TOKEN_NO_CACHE } from "src/app/core/http-context-tokens";
import { Parapharmaceutical } from "src/app/backend/models/parapharmaceutical";

@Injectable()
export class DrugBackend {
  private readonly http = inject(HttpClient);
  private readonly bootstrapService = inject(BootstrapService);

  load(drugId: string): Observable<Drug | Parapharmaceutical> {
    return this.http
      .get<Drug | Parapharmaceutical>(
        `${environment.snoots.api.healthcare}/drugs/${drugId}`
      )
      .pipe(
        map(
          (x) => <Drug | Parapharmaceutical>extendDoc(x, this.bootstrapService)
        )
      );
  }

  loadLightByMinsanNoCache(minsan: string): Observable<DrugLight | undefined> {
    return this.loadLightByMinsan(
      minsan,
      new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
    );
  }

  loadLightByMinsan(
    minsan: string,
    context?: HttpContext
  ): Observable<DrugLight | undefined> {
    return this.http
      .get<PaginatedResult<DrugLight>>(
        `${environment.snoots.api.healthcare}/drugs`,
        {
          params: {
            light: true,
            minsan
          },
          context
        }
      )
      .pipe(
        map((response) => first(response.docs)),
        map((x: DrugLight | undefined) =>
          x ? <DrugLight>extendDocLight(x, this.bootstrapService) : undefined
        )
      );
  }

  loadLightByEan(ean: string): Observable<DrugLight | undefined> {
    return this.http
      .get<PaginatedResult<DrugLight>>(
        `${environment.snoots.api.healthcare}/drugs`,
        {
          params: {
            light: true,
            ean
          }
        }
      )
      .pipe(
        map((response) => first(response.docs)),
        map((x: DrugLight | undefined) =>
          x ? <DrugLight>extendDocLight(x, this.bootstrapService) : undefined
        )
      );
  }

  loadLightByEanNoCache(ean: string): Observable<DrugLight | undefined> {
    return this.http
      .get<PaginatedResult<DrugLight>>(
        `${environment.snoots.api.healthcare}/drugs`,
        {
          headers: {
            ["snt-no-cache"]: "true"
          },
          params: {
            light: true,
            ean
          }
        }
      )
      .pipe(
        map((response) => first(response.docs)),
        map((x: DrugLight | undefined) =>
          x ? <DrugLight>extendDocLight(x, this.bootstrapService) : undefined
        )
      );
  }

  loadLightNoCache(id: string): Observable<DrugLight> {
    return this.loadLight(
      id,
      new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
    );
  }

  loadLight(id: string, context?: HttpContext): Observable<DrugLight> {
    return this.http
      .get<DrugLight>(`${environment.snoots.api.healthcare}/drugs/${id}`, {
        params: {
          light: true
        },
        context
      })
      .pipe(map((x) => <DrugLight>extendDocLight(x, this.bootstrapService)));
  }

  list(request: PageRequest): Observable<PaginatedResult<Drug>> {
    return this.http
      .get<PaginatedResult<Drug>>(
        `${environment.snoots.api.healthcare}/drugs`,
        {
          params: {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          }
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(
              (x) => <Drug>extendDoc(x, this.bootstrapService)
            )
          };
        })
      );
  }

  searchAll(name: string): Observable<Drug[]> {
    return this.search({ page: 1, pageSize: 10000 }, name).pipe(
      map((response) => response.docs)
    );
  }

  search(
    request: PageRequest,
    name: string
  ): Observable<PaginatedResult<Drug>> {
    return this.http
      .get<PaginatedResult<Drug>>(
        `${environment.snoots.api.healthcare}/drugs`,
        {
          params: {
            p: request.page,
            s: request.pageSize,
            name
          }
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(
              (x) => <Drug>extendDoc(x, this.bootstrapService)
            )
          };
        })
      );
  }

  searchAllLight(query: string): Observable<DrugLight[]> {
    return this.searchLight({}, query).pipe(map((response) => response.docs));
  }

  searchLight(
    request: PageRequest,
    name: string
  ): Observable<PaginatedResult<DrugLight>> {
    return this.http
      .get<PaginatedResult<DrugLight>>(
        `${environment.snoots.api.healthcare}/drugs`,
        {
          params: omitFalsyProperties({
            p: request.page,
            s: request.pageSize,
            light: true,
            name
          })
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(
              (x) => <DrugLight>extendDocLight(x, this.bootstrapService)
            )
          };
        })
      );
  }

  listForRateTable(
    request: PageRequest,
    requestDrugCoefficient: number,
    rateTableId: string
  ): Observable<PaginatedResult<Drug | Parapharmaceutical>> {
    return this.http
      .get<PaginatedResult<Drug | Parapharmaceutical>>(
        `${environment.snoots.api.healthcare}/drugs`,
        {
          params: {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          }
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(
              (x) =>
                <Drug | Parapharmaceutical>extendDoc(x, this.bootstrapService)
            )
          };
        })
      );
  }

  @UserNotify({
    successMessage: marker("Farmaco creato"),
    errorMessage: (error: any) => {
      if (error.status === 409) {
        return sntTranslate(marker("Famarco già presente"));
      }
      return sntTranslate(marker("Impossibile creare il Farmaco"));
    },
    progressIndeterminate: true,
    progressMessage: marker("Crezione farmaco")
  })
  create(payload: DrugCreate): Observable<Drug> {
    return this.http
      .post<Drug>(`${environment.snoots.api.healthcare}/drugs`, payload)
      .pipe(map((x) => <Drug>extendDoc(x, this.bootstrapService)));
  }

  @UserNotify({
    successMessage: marker("Farmaco aggiornato"),
    errorMessage: marker("Impossibile aggiornare il farmaco")
  })
  update(id: string, request: Partial<Drug>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/drugs/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Variazione aggiornata"),
    errorMessage: marker("Impossibile aggiornare la variazione")
  })
  updateVariation(
    id: string,
    payload: MedicalServiceUpdateVariations
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/drugs/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Farmaco disattivato"),
    errorMessage: marker("Impossibile disattivare il farmaco")
  })
  disabled(id: string, request: Partial<Drug>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/drugs/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Farmaco eliminato"),
    errorMessage: marker("Impossibile eliminare il farmaco")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/drugs/${id}`
    );
  }
}

function extendDoc(drug: Drug, bootstrapService: BootstrapService): Drug {
  const useFixedPrice =
    !!bootstrapService.getCurrentHealthcareConfiguration().drug?.useFixedPrice;
  if (useFixedPrice) {
    drug.unitPrice = drug.customUnitPrice ?? drug.unitPrice;
    drug.vat = drug.customVat ?? drug.vat;
    drug.rateable = drug.customRateable ?? drug.rateable;
  }
  if (drug.metadata) {
    drug.metadata.packaging = drug.metadata.packaging
      ? sanitizeHTML(drug.metadata.packaging)
      : drug.metadata.packaging;
  }

  return drug;
}

function extendDocLight(
  drug: DrugLight,
  bootstrapService: BootstrapService
): DrugLight {
  const useFixedPrice =
    !!bootstrapService.getCurrentHealthcareConfiguration().drug?.useFixedPrice;
  if (useFixedPrice) {
    drug.unitPrice = drug.customUnitPrice ?? drug.unitPrice;
    drug.vat = drug.customVat ?? drug.vat;
    drug.rateable = drug.customRateable ?? drug.rateable;
  }

  return drug;
}
